/**
 * @fileoverview Custom hook for managing login form state and submission
 */

import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../../context/AuthContext';
import { addRecentUser } from '../utils/recentUsers';

/**
 * Custom hook for managing the login form
 * @param {Object} options - Hook options
 * @param {Function} options.onRequires2FA - Callback when 2FA is required
 * @param {Function} options.onLoginSuccess - Callback on successful login
 * @param {Function} options.onError - Callback for error handling
 * @returns {Object} Form state and handlers
 */
export const useLoginForm = ({ onRequires2FA, onLoginSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .min(5, 'Username must be at least 5 characters')
        .max(15, 'Username must be at most 15 characters')
        .required('Username is required'),
      password: Yup.string()
        .required('Password is required'),
    }),
    onSubmit: async (values) => {
      setLoading(true);

      try {
        const result = await login(values.username, values.password);

        if (result.success) {
          // Case 1: First-time user needs to set up a passkey
          if (result.requiresPasskeySetup) {
            onRequires2FA({
              username: values.username,
              tempToken: result.tempToken,
              hasBiometric: false,
              hasEmail: result.hasEmail || false,
              needsPasskeySetup: true,
              passkeyPromptDismissed: result.passkeyPromptDismissed || false,
              preferEmail: false
            });
          }
          // Case 2: User has passkeys or prefers email - needs 2FA
          else if (result.requires2FA) {
            onRequires2FA({
              username: values.username,
              tempToken: result.tempToken,
              hasBiometric: result.hasBiometric || false,
              hasEmail: result.hasEmail || false,
              needsPasskeySetup: false,
              passkeyPromptDismissed: result.passkeyPromptDismissed || false,
              preferEmail: result.preferEmail || false
            });
          }
          // Case 3: Login successful without 2FA
          else {
            // Save to recent users
            addRecentUser({
              username: values.username,
              firstName: result.user?.first_name || '',
              lastName: result.user?.last_name || '',
              hasPasskey: false
            });

            onLoginSuccess({
              username: values.username,
              user: result.user
            });
          }
        } else {
          onError(result.error || 'Login failed');
        }
      } catch (err) {
        onError('An unexpected error occurred during login');
      } finally {
        setLoading(false);
      }
    },
  });

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  return {
    formik,
    loading,
    showPassword,
    togglePasswordVisibility,
    setLoading
  };
};

export default useLoginForm;
